import React, { useState } from 'react';
import { CheckCircle2, Loader2, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { WorkflowNode, AgentStepDetail } from '../types';
import { AgentDetailModal } from './agent-detail-modal';

interface WorkflowProgressProps {
  nodes: WorkflowNode[];
  isLive?: boolean;
}

const NODE_LABELS: Record<string, string> = {
  classify: 'Classifying intent',
  create_plan: 'Creating execution plan',
  execute_plan: 'Executing queries',
  evaluate: 'Evaluating results',
  generate_summary: 'Generating summary',
};

function formatNodeDetail(node: WorkflowNode): string | null {
  const d = node.details;
  if (!d) return null;

  switch (node.name) {
    case 'classify':
      if (d.intent) {
        const conf = d.intentConfidence != null
          ? ` (${Math.round(d.intentConfidence * 100)}%)`
          : '';
        return `Intent: ${d.intent}${conf}`;
      }
      return null;
    case 'create_plan':
      if (d.steps?.length) {
        const types = d.steps.map((s: { type: string }) => s.type).join(', ');
        const cycle = d.planningCycleCount ? ` · cycle ${d.planningCycleCount}` : '';
        return `${d.steps.length} step(s): ${types}${cycle}`;
      }
      return null;
    case 'execute_plan':
      if (d.steps?.length) {
        const deferred = d.hasDeferredSteps ? ' (some deferred)' : '';
        const executed = d.stepsExecuted ?? d.steps.length;
        return `${executed} completed${deferred}`;
      }
      return null;
    case 'evaluate':
      if (d.decision) {
        const conf = d.confidence != null
          ? ` · ${Math.round(d.confidence * 100)}% confidence`
          : '';
        return `${d.decision}${conf}`;
      }
      return null;
    case 'generate_summary':
      if (d.confidence != null) {
        return `${Math.round(d.confidence * 100)}% confidence`;
      }
      return null;
    default:
      return null;
  }
}

function getNodeReasoning(node: WorkflowNode) {
  const d = node.details;
  if (!d) return null;

  switch (node.name) {
    case 'classify':
      return d.intentReasoning || null;
    case 'create_plan':
      if (d.steps?.length) {
        return d.steps.map((s: { type: string; reason: string }) => s.reason).filter(Boolean).join('; ') || null;
      }
      return null;
    case 'execute_plan':
      if (d.steps?.length) {
        return d.steps.map((s: { type: string; instruction?: string; query?: Record<string, any> }) => {
          const hasQuery = s.query && Object.keys(s.query).length > 0;
          return {
            type: s.type,
            label: s.instruction ? `${s.type}: ${s.instruction}` : s.type,
            clickable: hasQuery,
            step: s as AgentStepDetail,
          };
        });
      }
      return null;
    case 'evaluate':
    case 'generate_summary':
      return d.reasoning || null;
    default:
      return null;
  }
}

export function WorkflowProgress({ nodes, isLive = false }: WorkflowProgressProps) {
  const [isExpanded, setIsExpanded] = useState(isLive);
  const [selectedStep, setSelectedStep] = useState<AgentStepDetail | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (nodes.length === 0) return null;

  const visibleNodes = isExpanded ? nodes : nodes.slice(0, 1);

  return (
    <div className="mb-2" data-testid="workflow-progress">
      {!isLive && nodes.length > 1 && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mb-1"
          data-testid="workflow-progress-toggle"
        >
          {isExpanded ? (
            <>Hide steps <ChevronUp className="h-3 w-3" /></>
          ) : (
            <>{nodes.length} workflow steps <ChevronDown className="h-3 w-3" /></>
          )}
        </button>
      )}

      <div className="space-y-1">
        {visibleNodes.map((node, idx) => {
          const isLast = idx === visibleNodes.length - 1;
          const isCurrent = isLive && isLast;
          const label = NODE_LABELS[node.name] || node.name;
          const detail = formatNodeDetail(node);

          return (
            <div
              key={`${node.name}-${node.timestamp}`}
              className="flex items-start gap-2 text-xs"
              data-testid={`workflow-node-${node.name}`}
            >
              {isCurrent ? (
                <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin mt-0.5 flex-shrink-0" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <span className={isCurrent ? 'text-blue-700 font-medium' : 'text-gray-600'}>
                  {label}
                </span>
                {detail && (
                  <span className="text-gray-400 ml-1">— {detail}</span>
                )}
                {!isCurrent && getNodeReasoning(node) && (
                  <div className="mt-1">
                    {Array.isArray(getNodeReasoning(node)) ? (
                      <div className="flex flex-wrap gap-1">
                        {getNodeReasoning(node).map((item, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              if (item.clickable) {
                                setSelectedStep(item.step);
                                setIsModalOpen(true);
                              }
                            }}
                            className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded transition-colors max-w-[250px] ${item.clickable
                                ? 'text-blue-600 hover:text-blue-700 hover:bg-blue-50 cursor-pointer'
                                : 'text-gray-500 cursor-default'
                              }`}
                            disabled={!item.clickable}
                            title={item.clickable ? 'Click to view query and data' : undefined}
                          >
                            <span className="truncate">{item.label}</span>
                            {item.clickable && <ExternalLink className="h-2.5 w-2.5" />}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[10px] text-gray-400 italic leading-tight">
                        {getNodeReasoning(node)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AgentDetailModal
        step={selectedStep}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedStep(null);
        }}
      />
    </div>
  );
}
